import {
  UserProfile,
  withPageAuthRequired,
  WithPageAuthRequiredOptions
} from "@auth0/nextjs-auth0";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useState } from "react";
import styled from "styled-components";
import Breadcrumbs from "../components/breadcrumbs/Breadcrumbs";
import { ContributePageTitle } from "../components/contribute/ContributePageTitle";
import ContributionInstructions from "../components/contribute/ContributionInstructions";
import ContributionSuccess from "../components/contribute/ContributionSuccess";
import ContributeForm from "../components/contribute/form/ContributeForm";
import { ContributionFormHeading } from "../components/contribute/form/ContributionFormHeading";
import { Templates } from "../components/contribute/form/form-schema";
import {
  CATEGORY_ICONS,
  CATEGORY_SINGULAR_LABEL,
  isKnownCategory
} from "../components/navigation/shared";
import { PageContainer } from "../components/page-partials/PageContainer";
import createServerContext from "../context/server-context";
import { findItemById, getItemFacets } from "../data/db";
import { jsonify } from "../data/db/jsonify";
import { Contribution, ContributionSource, Facets } from "../data/db/types";
import { checkOwnership, getCurrentUser } from "../data/db/user";
import { itemToContributionSource } from "../data/github/utils";
import { setCacheHeader } from "../page-cache";
import { pxToRem } from "@input-output-hk/px-to-rem";
import { toBool } from "../utils/env";
import { Maybe, Nullable } from "../utils/types";
import { getContribution } from "./api/contribute/[id]";

const IconContainer = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--default-text-color-invert);
  background-color: var(--default-text-color);
  width: ${pxToRem(40)};
  height: ${pxToRem(40)};
  border-radius: 50%;
  margin-right: ${pxToRem(12)};
  flex-shrink: 1;
`;

const ContributeFormWrapper = styled.div`
  max-width: ${pxToRem(780)};
  margin: auto;
  margin-bottom: ${pxToRem(100)};
`;

const CAN_CONTRIBUTE = toBool(process.env.NEXT_PUBLIC_ENABLE_CONTRIBUTE);

type Props = {
  user: Nullable<UserProfile>;
  facets: Maybe<Facets>;
  contribution: Maybe<Contribution | ContributionSource>;
};

const BaseContributePage: React.FC<Props> = ({ contribution, facets }) => {
  const [saved, setSaved] = useState<Contribution | null>(null);
  const router = useRouter();
  const category = router.query.category as string | undefined;
  const defaultCategory = isKnownCategory(category) ? category : "article";
  const schema = Templates[defaultCategory];
  const label = CATEGORY_SINGULAR_LABEL[defaultCategory];
  const Icon = CATEGORY_ICONS[defaultCategory];
  const formattedLabel =
    label === "article" || label === "FAQ" || label === "infographic"
      ? `Add an ${label}`
      : label === "other resource"
      ? `Add another type of resource`
      : `Add a ${label}`;
  const title = saved ? "Your contribution has been added" : formattedLabel;
  const heading = `Please add details about your ${label}`;

  console.log(router.asPath);

  return (
    <>
      <Breadcrumbs
        breadcrumbArgument={{
          argumentType: "contributionType",
          argumentContents: label
        }}
      />
      <PageContainer>
        <Head>
          <title>Contribute | Essential Cardano</title>
          <meta name="description" content="Essential Cardano" />
          <meta name="robots" content="noindex, nofollow" />
          <link rel="icon" href="/favicon.ico" />
          <link
            rel="icon"
            type="image/png"
            sizes="32x32"
            href="/favicon-32x32.png"
          />
          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href="/favicon-16x16.png"
          />
          <link
            rel="canonical"
            href={`https://www.essentialcardano.io${router.asPath}`}
            key="canonical"
          />
        </Head>
        <ContributePageTitle>
          <IconContainer>
            <Icon />
          </IconContainer>
          {title}
        </ContributePageTitle>
        {CAN_CONTRIBUTE ? (
          saved ? (
            <ContributeFormWrapper>
              <ContributionSuccess
                contribution={saved}
                onClickCta={() => setSaved(null)}
              />
            </ContributeFormWrapper>
          ) : (
            <ContributeFormWrapper>
              <ContributionFormHeading>{heading}</ContributionFormHeading>
              <ContributeForm
                enabled={true}
                contribution={contribution}
                facets={facets}
                defaultCategory={defaultCategory}
                schema={schema}
                onSaveSuccess={(s) => setSaved(s)}
              />
            </ContributeFormWrapper>
          )
        ) : (
          <ContributionInstructions />
        )}
      </PageContainer>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (
  baseCtx
) => {
  const ctx = createServerContext(baseCtx);
  const user = await getCurrentUser(ctx);
  const facets = await getItemFacets(ctx);
  const _id = baseCtx.query._id as string;
  const _sourceId = baseCtx.query._sourceId as string;

  setCacheHeader(ctx, baseCtx.res);

  if (user) {
    if (_id) {
      const { submission } = await getContribution(ctx, _id, {
        withComments: false
      });
      const isOwner = await checkOwnership(ctx, submission.agent);
      if (isOwner) {
        return {
          props: {
            contribution: jsonify(submission),
            user: jsonify(user),
            facets
          }
        };
      }
    } else if (_sourceId) {
      const item = await findItemById(ctx, _sourceId);
      if (item) {
        const contributionSource = itemToContributionSource(item);
        return {
          props: {
            contribution: contributionSource,
            user: jsonify(user),
            facets
          }
        };
      }
    }
  }

  return {
    props: {
      contribution: null,
      user: user ? jsonify(user) : null,
      facets
    }
  };
};

/**
 * If the user is not logged in, we need
 * to restric the contribte form
 */
const ContributePage = CAN_CONTRIBUTE
  ? withPageAuthRequired<Props>(
      BaseContributePage as WithPageAuthRequiredOptions<Props>
    )
  : BaseContributePage;

export default ContributePage;
